import {type FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {reportToSentry} from './sentry';
import {getErrorDescription} from './unexpected-state-errors';

export class SweetError extends Error {
  response?: {status: number; url: string};
  data: any;
  detail: any | null | undefined;
}

export interface SweetBadRequestError {
  status: 400;
  data: {code: string};
}

const thereIsConsole = window.console != null;

const safeConsole = {
  /* eslint-disable no-console */
  warn: (...args) => thereIsConsole && console.warn(...args),
  error: (...args) => thereIsConsole && console.error(...args),
  /* eslint-enable no-console */
};

export function reportUnexpectedState(
  errorCode: string,
  extra?: Record<string, any>
) {
  const description = getErrorDescription(errorCode);
  const error = new Error(description);
  error.name = 'UnexpectedStateError';

  if (extra != null) {
    safeConsole.warn('UnexpectedStateError extra data', extra);
  }

  if (process.env.NODE_ENV === 'production') {
    reportToSentry(error, extra, true);
    safeConsole.error(error);
  }

  if (process.env.NODE_ENV === 'development') {
    throw error;
  }
}

export function assertExpectedState(
  condition: boolean,
  description: string,
  additionalData?: Record<string, any>
) {
  if (condition === false) {
    reportUnexpectedState(description, additionalData);
  }
}

export function isFetchBaseQueryError(
  error: unknown
): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export function isFetchBaseQueryBadRequestCode(
  error: unknown
): error is SweetBadRequestError {
  if (isFetchBaseQueryError(error)) {
    return (
      error.status === 400 &&
      typeof error.data === 'object' &&
      error.data !== null &&
      'code' in error.data &&
      typeof (error.data as any).code === 'string'
    );
  }
  return false;
}
